import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Image, Coins, TrendingUp, Trophy, Store, ChevronDown } from 'lucide-react';
import { useGame, api } from '../context/GameContext';
import { toast } from 'sonner';

// Tutorial funnel: best-effort fire-and-forget so a network hiccup
// never blocks navigation. Backend deduplicates per 30-min window so
// extra calls are harmless.
const fireTutEvent = (event) => {
  try {
    api.post('/analytics/tut-event', { event }).catch(() => {});
  } catch (_) { /* noop */ }
};

const BottomNav = () => {
  const { t, haptic, tutorialStep, setTutorialStep, language } = useGame();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', icon: Image, label: 'collection' },
    { path: '/earn', icon: Coins, label: 'earn' },
    { path: '/upgrades', icon: TrendingUp, label: 'upgrades' },
    { path: '/top', icon: Trophy, label: 'top' },
    { path: '/market', icon: Store, label: 'auction' },
  ];

  return (
    <nav 
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 glass border-t border-white/10 safe-bottom z-40"
    >
      <div className="max-w-md mx-auto flex items-center justify-around py-2 px-1 w-full">
        {navItems.map((item) => {
          const isActive = !item.comingSoon && location.pathname === item.path;
          const Icon = item.icon;
          const isTutorialHighlight = 
            (tutorialStep === 'earn' && item.label === 'earn') ||
            (tutorialStep === 'upgrades' && item.label === 'upgrades') ||
            (tutorialStep === 'gallery' && item.label === 'collection');
          
          if (item.comingSoon) {
            return (
              <button
                key={item.label}
                data-testid={`nav-${item.label}`}
                onClick={() => {
                  haptic();
                  toast(t('coming_soon'), {
                    description: t('coming_soon'),
                    duration: 2000,
                  });
                }}
                className="flex flex-col items-center py-2 px-1.5 rounded-xl transition-all text-[#A1A1AA] hover:text-[#F5F5F7] flex-1 min-w-0"
              >
                <Icon className="w-6 h-6 mb-1" strokeWidth={1.5} />
                <span className="text-[10px] font-medium truncate w-full text-center">{t('auction')}</span>
              </button>
            );
          }
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => {
                haptic();
                if (isTutorialHighlight && setTutorialStep) {
                  if (tutorialStep === 'earn') {
                    fireTutEvent('tut_earn_tab_opened');
                    setTutorialStep('discover_claim');
                  } else if (tutorialStep === 'upgrades') {
                    fireTutEvent('tut_upgrades_tab_opened');
                    setTutorialStep('upgrade_slots');
                  } else if (tutorialStep === 'gallery') {
                    fireTutEvent('tut_gallery_tab_opened');
                    setTutorialStep('buy_painting');
                  }
                }
              }}
              data-testid={`nav-${item.label}`}
              className={`flex flex-col items-center py-2 px-1.5 rounded-xl transition-all relative flex-1 min-w-0 ${
                isTutorialHighlight
                  ? 'text-[#D4AF37]'
                  : isActive 
                  ? 'text-[#D4AF37]' 
                  : 'text-[#A1A1AA] hover:text-[#F5F5F7]'
              }`}
            >
              {isTutorialHighlight && (
                <>
                  {/* Layered soft highlight: warm spotlight + two slowly
                      expanding rings, instead of a hard gold rectangle. */}
                  <span className="tut-spotlight" aria-hidden="true" />
                  <span className="tut-ring" aria-hidden="true" />
                  <span className="tut-ring tut-ring-delayed" aria-hidden="true" />
                  <div
                    data-testid={`nav-arrow-${item.label}`}
                    className="absolute -top-8 left-1/2 pointer-events-none animate-tut-float z-50"
                  >
                    <ChevronDown
                      className="w-7 h-7 text-[#F5D07A]"
                      strokeWidth={2.4}
                      style={{ filter: 'drop-shadow(0 0 10px rgba(245, 208, 122, 0.8))' }}
                    />
                  </div>
                </>
              )}
              <Icon
                className={`w-6 h-6 mb-1 transition-transform relative ${
                  isTutorialHighlight ? 'tut-bob' : isActive ? 'scale-110' : ''
                }`}
                strokeWidth={isActive || isTutorialHighlight ? 2 : 1.5}
                style={isTutorialHighlight
                  ? { filter: 'drop-shadow(0 0 8px rgba(245, 208, 122, 0.65))' }
                  : undefined}
              />
              <span className={`text-[10px] font-medium truncate w-full text-center relative ${
                isTutorialHighlight ? 'text-[#F5D07A]' : ''
              }`}>
                {t(item.label)}
              </span>
              {isActive && (
                <div className="absolute -bottom-0 w-1 h-1 rounded-full bg-[#D4AF37]" />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
